import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import type React from 'react';
import { useRef } from 'react';

export interface RangeSliderProps {
    min?: number;
    max?: number;
    value?: number | string;
    setValue?: (value: number) => void;
    unit?: string;
    disabled?: boolean;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({ min = 0, max = 20, value: rawValue, setValue, unit = '%', disabled = false }) => {
    if (rawValue === undefined || setValue === undefined) {
        throw new Error('RangeSlider: value and setValue are required');
    }

    // Parse la valeur (peut être string depuis l'API)
    const value = typeof rawValue === 'string' ? parseFloat(rawValue) || 0 : rawValue;

    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const sliderKnobRef = useRef<HTMLDivElement>(null);

    const updateSliderValue = (clientX: number): void => {
        if (disabled) return;
        const slider = sliderContainerRef.current;
        if (!slider) return;

        const { left, width } = slider.getBoundingClientRect();
        const positionX = Math.max(0, Math.min(clientX - left, width));
        const newValue = min + (positionX / width) * (max - min);
        setValue(Math.round(newValue));
    };

    const startSliderInteraction = (clientX: number): void => {
        updateSliderValue(clientX);

        const moveSlider = (e: MouseEvent | TouchEvent): void => {
            const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
            updateSliderValue(clientX);
        };

        const stopSliderInteraction = (): void => {
            document.removeEventListener('mousemove', moveSlider);
            document.removeEventListener('mouseup', stopSliderInteraction);
            document.removeEventListener('touchmove', moveSlider);
            document.removeEventListener('touchend', stopSliderInteraction);
        };

        document.addEventListener('mousemove', moveSlider);
        document.addEventListener('mouseup', stopSliderInteraction);
        document.addEventListener('touchmove', moveSlider);
        document.addEventListener('touchend', stopSliderInteraction);
    };

    const incrementSliderValue = (): void => {
        if (disabled) return;
        setValue(Math.min(max, value + 1));
    };
    const decrementSliderValue = (): void => {
        if (disabled) return;
        setValue(Math.max(min, value - 1));
    };

    const sliderPercentage = ((value - min) / (max - min)) * 100;

    return (
        <div className={`flex items-center gap-3 w-full ${disabled ? 'opacity-60' : ''}`}>
            <ButtonSlider onClick={decrementSliderValue} disabled={disabled}>
                <MinusIcon className="h-4 w-4" />
            </ButtonSlider>
            <div
                className={`flex items-center h-10 flex-1 relative ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={(e) => {
                    if (!disabled) updateSliderValue(e.clientX);
                }}
                ref={sliderContainerRef}
            >
                <div className={`${disabled ? 'bg-gray-400' : 'bg-primary'} h-1 rounded-l-lg`} style={{ width: `${sliderPercentage}%` }} />
                <div
                    className={`${disabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary cursor-move'} shadow-sm h-3 w-3 rounded-full absolute left-0 select-none`}
                    onMouseDown={(e) => {
                        if (disabled) return;
                        e.stopPropagation();
                        startSliderInteraction(e.clientX);
                    }}
                    onTouchStart={(e) => {
                        if (disabled) return;
                        e.stopPropagation();
                        startSliderInteraction(e.touches[0].clientX);
                    }}
                    ref={sliderKnobRef}
                    style={{ touchAction: 'none', left: `${sliderPercentage}%` }}
                />
                <div className="bg-border rounded-r-lg flex-1 h-1" />
                <div className={`absolute -top-1 right-0 transform text-xs ${disabled ? 'text-gray-400' : 'text-primary'}`}>
                    {value}
                    {unit}
                </div>
            </div>
            <ButtonSlider onClick={incrementSliderValue} disabled={disabled}>
                <PlusIcon className="h-4 w-4" />
            </ButtonSlider>
        </div>
    );
};

function ButtonSlider({ onClick, children, disabled }: { onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
    return (
        <button
            className={`h-6 w-6 rounded-full inline-flex items-center justify-center ${
                disabled
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-card text-secondary hover:bg-primary hover:text-white'
            }`}
            onClick={onClick}
            type="button"
            disabled={disabled}
        >
            {children}
        </button>
    );
}
